/**
 * Shapes what the consoles send to PostHog error tracking: drops browser noise
 * nobody can act on, names the deployed environment (dev and production report
 * to the same PostHog project), and redacts credentials from the context a
 * caller attaches to an exception.
 */

import type { CaptureResult } from 'posthog-js';

/**
 * Error messages that are noise: network blips, ResizeObserver chatter and
 * cross-origin scripts that hide their detail. Matched as substrings of the
 * error text, so they stay in English.
 */
export const IGNORED_ERROR_MESSAGES = [
  'Non-Error promise rejection captured',
  'NetworkError',
  'Failed to fetch',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Load failed', // Safari network errors
  'Script error', // Cross-origin script errors with no detail
];

/** Context keys whose values never leave the browser, at any depth. */
const SENSITIVE_KEYS = new Set([
  'password',
  'token',
  'apikey',
  'secret',
  'authorization',
]);

const REDACTED = '[REDACTED]';

// Deep enough for any context a caller attaches; a cycle stops here too.
const MAX_DEPTH = 8;

interface ExceptionItem {
  type?: unknown;
  value?: unknown;
}

function errorText(properties: Record<string, unknown>): string {
  const list = properties.$exception_list;
  const first = Array.isArray(list) ? (list[0] as ExceptionItem | undefined) : undefined;
  return [first?.value, first?.type, properties.$exception_message]
    .filter((part): part is string => typeof part === 'string')
    .join(' ');
}

/**
 * Copies a caller's context with every sensitive key's value replaced. Applied
 * to what captureError and bug reports attach, never to a whole event: the SDK
 * carries the project key in its own `token` property.
 */
export function redactContext(
  context: Record<string, unknown>,
): Record<string, unknown> {
  return redact(context, 0) as Record<string, unknown>;
}

function redact(value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redact(item, depth + 1));
  }
  // Only plain objects are walked: copying an Error or a Date through
  // Object.entries would keep none of what makes it one.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    out[key] = SENSITIVE_KEYS.has(key.toLowerCase())
      ? REDACTED
      : redact(item, depth + 1);
  }
  return out;
}

/**
 * The `before_send` step for error tracking. Returns null to drop the event;
 * every event that is not an exception passes through untouched.
 *
 * A bug report a person files is never noise, whatever its text says.
 */
export function prepareExceptionEvent(
  event: CaptureResult | null,
  environment: string,
): CaptureResult | null {
  if (!event || event.event !== '$exception') {
    return event;
  }
  const properties = (event.properties ?? {}) as Record<string, unknown>;
  if (properties.report_type !== 'bug_report') {
    const text = errorText(properties);
    if (IGNORED_ERROR_MESSAGES.some((ignored) => text.includes(ignored))) {
      return null;
    }
  }
  return { ...event, properties: { ...properties, environment } };
}
